import {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  FocusEvent,
} from "react"
import { useI18n } from "providers/I18n"
import { styles } from "./styles"
import { TextInput } from "components/inputs/TextInput"
import get from "lodash/fp/get"
import assoc from "lodash/fp/set"
import { queryCache, useMutation, useQuery } from "react-query"
import { fetchAgency, updateMarketAgency } from "api/agencies"
import { useParams } from "react-router"
import { Agency, AgencyContactPhone } from "models/agency"
import { Countries } from "routes/Base/Admin/MarketVendors/Details/Profile"
import { useAuthenticated } from "providers/A12n"
import { Loading } from "components/Loading"
import { ContactNumber } from "./ContactNumbers"
import { throttle } from "lodash"
import { ErrorsMap } from "api/http/utils"

const InitialState = {
  id: "",
  name: "",
  code: "",
  contact: {},
}

const query_key = "agency-details"
function fetchOptions(_: string, id: string): Promise<Agency> {
  return fetchAgency(query_key, id)
}

const fetchOptionsDebounced = throttle(fetchOptions, 200)

function Profile() {
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const { hasAuthority } = useAuthenticated()
  const [state, setState] = useState<Partial<Agency>>(() => InitialState)
  const [errors, setErrors] = useState<Record<string, ErrorsMap | undefined>>()

  const {
    data: agency,
    isLoading,
    updatedAt,
  } = useQuery([query_key, id], fetchOptionsDebounced)

  const [patch] = useMutation(updateMarketAgency, {
    onSuccess: () => queryCache.invalidateQueries("agency-details"),
  })

  const handleChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => {
    const { name, value } = evt?.target
    !!name && setState((s) => assoc(name, value, s))
  }

  const handleCountryChange = (value: string, name?: string) => {
    !!name && setState((s) => assoc(name, value, s))
  }

  const handleBlur = useCallback(
    (section?: "address") =>
      (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name = "", value = "" } = evt?.target ?? {}
        !!agency &&
          value !== get(name, agency) &&
          patch({
            id: agency?.id,
            [section || name]: state[section || (name as keyof Agency)],
          })
      },
    [patch, agency, state],
  )

  const handleCountryBlur = useCallback(() => {
    !!agency &&
      state?.address?.countryCode !== get("address.countryCode", agency) &&
      patch({
        id: agency?.id,
        ["address" as keyof Agency]: state["address" as keyof Agency],
      })
  }, [agency, patch, state])

  const onContactUpdate = (
    type: "phones" | "faxes",
    pld: AgencyContactPhone[],
  ) =>
    patch({
      id: agency?.id ?? "",
      contact: { ...agency?.contact, [type]: pld },
    })

  const onContactStateUpdate = (
    type: "phones" | "faxes",
    pld: AgencyContactPhone[],
  ) => setState((s) => ({ ...s, contact: { ...s?.contact, [type]: pld } }))

  const handleContactValidation = (type: string, idx: number) =>
    setErrors((e) => ({
      ...e,
      [type]: { [idx]: `market-agencies.profile.contact.${type}.number.empty` },
    }))

  const onClearContactErrors = (type: string, idx: number) => {
    setErrors((err) =>
      Object.keys(err?.[type] ?? {})
        .filter((key) => key !== `${idx}`)
        .reduce((obj: Record<string, any>, key) => {
          obj[key] = err?.[type]?.[key]
          return obj
        }, {}),
    )
  }

  useEffect(() => {
    setState(agency ?? {})
    setErrors(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  return isLoading ? (
    <Loading />
  ) : (
    <div className={styles.main}>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t("vendors.profile.section.details")}
        </span>
        <div>
          <TextInput
            name={"localName"}
            size="medium"
            className={styles.detailsInput}
            label={t("market-agencies.list.local-name")}
            margin="normal"
            value={state?.localName ?? ""}
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
          <TextInput
            name="code"
            value={state?.code ?? ""}
            label={t("market-agencies.list.code")}
            className={styles.titleInput}
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            margin="normal"
            size="medium"
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
          <TextInput
            name={"localCode"}
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            size="medium"
            className={styles.detailsInput}
            label={t("market-agencies.list.local-code")}
            margin="normal"
            value={state?.localCode ?? ""}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
        </div>
        <div>
          <TextInput
            name={"marketName"}
            size="medium"
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            className={styles.detailsInput}
            label={t("market-agencies.list.market")}
            margin="normal"
            value={state?.marketName ?? ""}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
          <TextInput
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            name={"subRegionName"}
            size="medium"
            className={styles.detailsInput}
            label={t("market-agencies.list.sub-region")}
            margin="normal"
            value={state?.subRegionName ?? ""}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
          <TextInput
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            name={"regionName"}
            size="medium"
            className={styles.detailsInput}
            label={t("market-agencies.list.region")}
            margin="normal"
            value={state.regionName ?? ""}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
        </div>
        <div>
          <TextInput
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            name={"profitCenter"}
            size="medium"
            className={styles.detailsInput}
            label={t("market-agencies.list.profit-center")}
            margin="normal"
            value={state?.profitCenter ?? ""}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
          <TextInput
            onChange={handleChange}
            onBlur={(e) => handleBlur()(e)}
            name={"costCenter"}
            size="medium"
            className={styles.detailsInput}
            label={t("market-agencies.list.cost-center")}
            margin="normal"
            value={state?.costCenter ?? ""}
            disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
          />
        </div>
      </section>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t("market-agencies.profile.addresses")}
        </span>
        <div className={styles.addresses}>
          {t("market-agencies.profile.business-address")}
        </div>
        <div className={styles.inputsWrapper}>
          <div className={styles.address}>
            <div>
              <TextInput
                name="address.address1"
                value={state?.address?.address1 ?? ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                placeholder={t("market-agencies.profile.address1")}
                fullWidth
                label={t("market-agencies.profile.address1")}
                margin="normal"
                size="medium"
                className={styles.adress ?? ""}
                disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
              />
            </div>
            <div>
              <TextInput
                name="address.address2"
                value={state?.address?.address2 ?? ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                placeholder={t("market-agencies.profile.address2")}
                fullWidth
                label={t("market-agencies.profile.address2")}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
              />
            </div>
            <div className={styles.dropdownInput}>
              <Countries
                label={t("market-agencies.fields.address.countryCode")}
                name="address.countryCode"
                regionCode={state?.["regionCode"] ?? ""}
                value={state?.address?.countryCode || ""}
                onValueChange={(v, n) => handleCountryChange(v, n)}
                onBlur={handleCountryBlur}
                disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
              />
              <TextInput
                name="address.state"
                value={state?.address?.state ?? ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                placeholder={t("market-agencies.profile.state")}
                label={t("market-agencies.profile.state")}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
              />
              <TextInput
                name="address.city"
                value={state?.address?.city ?? ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                placeholder={t("market-agencies.profile.city")}
                label={t("market-agencies.profile.city")}
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
              />
              <TextInput
                name="address.zipCode"
                value={state?.address?.zipCode ?? ""}
                onChange={handleChange}
                onBlur={(e) => handleBlur("address")(e)}
                placeholder={t("market-agencies.profile.zipCode")}
                label={t("market-agencies.profile.zipCode")}
                className={styles.zip}
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
              />
            </div>
          </div>
        </div>
      </section>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t("market-agencies.profile.contact")}
        </span>
        <div className={"BodyBody-2BlackLight"}>
          {t("market-agencies.profile.contact.subtitle")}
        </div>
        <ContactNumber
          contact={agency?.contact}
          type="phones"
          onContactUpdate={onContactUpdate}
          onStateUpdate={onContactStateUpdate}
          data={state?.contact?.["phones"]}
          errors={errors}
          onClearErrors={onClearContactErrors}
          handleValidation={handleContactValidation}
        />
        <ContactNumber
          contact={agency?.contact}
          type="faxes"
          onContactUpdate={onContactUpdate}
          onStateUpdate={onContactStateUpdate}
          data={state?.contact?.["faxes"]}
          errors={errors}
          onClearErrors={onClearContactErrors}
          handleValidation={handleContactValidation}
        />
      </section>
    </div>
  )
}

export default Profile
